import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { getCurrentUser, logout as apiLogout } from "@/lib/api/auth";
import { queryCache } from "@/lib/api/queryCache";
import type { User } from "@/lib/api/types";
import i18n, { applyDocumentLanguage, isRtl, LANGUAGE_STORAGE_KEY, type Language } from "@/lib/i18n";
import { registerAdminPush, unregisterAdminPush, refreshAdminPush } from "@/lib/push";

type Theme = "light" | "dark";

const THEME_KEY = "seen.theme";

interface AppContextValue {
  theme: Theme;
  toggleTheme: () => void;
  language: Language;
  /** Alias of `language` used by admin screens. */
  lang: Language;
  setLanguage: (lang: Language) => void;
  rtl: boolean;
  user: User | null;
  loadingAuth: boolean;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
  transitioning: boolean;
  setTransitioning: (value: boolean) => void;
}

// Keep a single context instance alive across hot module reloads. Without this,
// an HMR update to this module creates a brand-new context object while the
// already-mounted <AppProvider> still publishes to the old one, so consumers
// read `null` and throw "useApp must be used inside <AppProvider>".
const globalScope = globalThis as typeof globalThis & {
  __seenAppContext?: React.Context<AppContextValue | null>;
};

const AppContext =
  globalScope.__seenAppContext ??
  (globalScope.__seenAppContext = createContext<AppContextValue | null>(null));




export function AppProvider({ children }: { children: ReactNode }) {
  const { i18n: instance } = useTranslation();
  const [theme, setTheme] = useState<Theme>("light");
  const [language, setLanguageState] = useState<Language>("en");
  const [user, setUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [transitioning, setTransitioning] = useState(false);

  // Hydrate persisted preferences after mount so SSR markup stays stable.
  useEffect(() => {
    const storedTheme = window.localStorage.getItem(THEME_KEY) as Theme | null;
    if (storedTheme === "dark" || storedTheme === "light") setTheme(storedTheme);

    const storedLang = window.localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
    const initial: Language = storedLang === "ar" ? "ar" : "en";
    setLanguageState(initial);
    void i18n.changeLanguage(initial);
    applyDocumentLanguage(initial);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const setLanguage = useCallback(
    (lang: Language) => {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      // Show the animated "?" interstitial, swap mid-way, then reveal.
      setTransitioning(true);
      window.setTimeout(() => {
        setLanguageState(lang);
        void instance.changeLanguage(lang);
        applyDocumentLanguage(lang);
        // Clear the API query cache so all API calls refetch in the new language!
        queryCache.clear();
      }, 600);
      window.setTimeout(() => setTransitioning(false), 1500);
    },
    [instance],
  );


  const refreshAuth = useCallback(async () => {
    setLoadingAuth(true);
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      // Keep legacy query consumers synchronized with the authoritative auth
      // context. In particular, replace a cached pre-login `null` immediately.
      queryCache.set(["current-user"], currentUser);
    } finally {
      setLoadingAuth(false);
    }
  }, []);

  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.role === "operator";

  useEffect(() => {
    if (!isAdmin) return;
    registerAdminPush();

    const refresh = () => {
      if (document.visibilityState === "visible") refreshAdminPush();
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [isAdmin]);

  const signOut = useCallback(async () => {
    await unregisterAdminPush().catch(() => {});
    await apiLogout();
    setUser(null);
    queryCache.clear();
    queryCache.set(["current-user"], null);
    // Clear any cached session from localStorage
    window.localStorage.removeItem('seen.session');
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      theme,
      toggleTheme: () => setTheme((t) => (t === "dark" ? "light" : "dark")),
      language,
      lang: language,
      setLanguage,
      rtl: isRtl(language),
      user,
      loadingAuth,
      refreshAuth,
      signOut,
      transitioning,
      setTransitioning,
    }),
    [theme, language, setLanguage, user, loadingAuth, refreshAuth, signOut, transitioning],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
}
